import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Platform brand colours ────────────────────────────────────────────────────
const PLATFORM_COLORS = {
  JioHotstar: "#1a6fff",
  Hotstar:    "#1a6fff",
  SonyLIV:    "#0077db",
  Cricbuzz:   "#ea580c",
  Willow:     "#16a34a",
  ESPN:       "#cc0000",
  Star:       "#e11d48",
  default:    "#6b7280",
};

// ── Cricket nation flags ──────────────────────────────────────────────────────
const COUNTRY_FLAGS = {
  india: "🇮🇳",
  australia: "🇦🇺",
  england: "🏴󠁧󠁢󠁥󠁮󠁧󠁿",
  pakistan: "🇵🇰",
  "new zealand": "🇳🇿",
  "south africa": "🇿🇦",
  "west indies": "🌴",
  "sri lanka": "🇱🇰",
  bangladesh: "🇧🇩",
  afghanistan: "🇦🇫",
  zimbabwe: "🇿🇼",
  ireland: "🇮🇪",
  scotland: "🏴󠁧󠁢󠁳󠁣󠁴󠁿",
  netherlands: "🇳🇱",
  uae: "🇦🇪",
  nepal: "🇳🇵",
  oman: "🇴🇲",
  usa: "🇺🇸",
  canada: "🇨🇦",
  kenya: "🇰🇪",
  namibia: "🇳🇦",
  "papua new guinea": "🇵🇬",
};

// ── Cricket team abbreviations ─────────────────────────────────────────────────
const TEAM_ABBREVS = {
  india: "IND",
  australia: "AUS",
  england: "ENG",
  pakistan: "PAK",
  "new zealand": "NZ",
  "south africa": "SA",
  "west indies": "WI",
  "sri lanka": "SL",
  bangladesh: "BAN",
  afghanistan: "AFG",
  zimbabwe: "ZIM",
  ireland: "IRE",
  scotland: "SCO",
  netherlands: "NED",
  uae: "UAE",
  nepal: "NEP",
  oman: "OMA",
  usa: "USA",
  canada: "CAN",
};

function getFlag(teamName) {
  const key = (teamName || "").toLowerCase();
  for (const [nation, flag] of Object.entries(COUNTRY_FLAGS)) {
    if (key.includes(nation)) return flag;
  }
  return "🏏";
}

function getAbbrev(teamName) {
  const key = (teamName || "").toLowerCase();
  for (const [nation, abbrev] of Object.entries(TEAM_ABBREVS)) {
    if (key.includes(nation)) return abbrev;
  }
  // Fallback: first 3 chars of first word, upper-cased
  return (teamName || "TBA").split(/\s+/)[0].slice(0, 3).toUpperCase();
}

/**
 * Given a list of score objects and two team names, return a map
 * { teamA: ["250/3 (45.2)", ...], teamB: [...] }
 */
function parseScoresByTeam(scores, teamA, teamB) {
  const out = { [teamA]: [], [teamB]: [] };

  for (const sc of (scores || [])) {
    // CricAPI inning names look like "India Inning 1" or "Australia Innings 2"
    const namePart = (sc.inning || "")
      .replace(/\s+(inning|innings)\s*\d*$/i, "")
      .trim();
    const nLower = namePart.toLowerCase();
    const aFirst = (teamA || "").toLowerCase().split(" ")[0];
    const bFirst = (teamB || "").toLowerCase().split(" ")[0];

    const scoreStr = `${sc.runs}/${sc.wickets} (${Number(sc.overs || 0).toFixed(1)})`;

    if (aFirst && nLower.includes(aFirst)) {
      out[teamA].push(scoreStr);
    } else if (bFirst && nLower.includes(bFirst)) {
      out[teamB].push(scoreStr);
    } else {
      // Fallback: assign alternately
      const total = out[teamA].length + out[teamB].length;
      if (total % 2 === 0) out[teamA].push(scoreStr);
      else out[teamB].push(scoreStr);
    }
  }

  return out;
}

// ── Shared helpers ────────────────────────────────────────────────────────────
function getStatusStyle(color) {
  if (color === "green") return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
  if (color === "blue")  return "bg-blue-500/15 text-blue-400 border-blue-500/25";
  return "bg-neutral-700/40 text-neutral-400 border-neutral-700/50";
}

function LiveDot() {
  return (
    <span className="relative flex h-2 w-2 mr-1.5 flex-shrink-0">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
    </span>
  );
}

function formatDate(date_gmt) {
  if (!date_gmt) return "";
  try {
    // CricAPI returns dateTimeGMT without a timezone suffix — force UTC parsing
    // by appending "Z" so browsers don't misinterpret it as local time.
    const utcStr = /[Zz]$|[+-]\d{2}:\d{2}$/.test(date_gmt) ? date_gmt : date_gmt + "Z";
    return new Date(utcStr).toLocaleString("en-IN", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      timeZone: "Asia/Kolkata",
      hour12: true,
    }) + " IST";
  } catch (_) {
    return "";
  }
}

// ── Cricket Card ──────────────────────────────────────────────────────────────
function CricketCard({ match, dimmed = false }) {
  const {
    series_name, match_type, teams, scores,
    is_live, is_result, is_upcoming,
    status_label, status_color,
    watch_platform, watch_url,
    date_gmt, result_text,
  } = match;

  const teamA = teams?.[0] || "TBA";
  const teamB = teams?.[1] || "TBA";
  const flagA  = getFlag(teamA);
  const flagB  = getFlag(teamB);
  const abbrevA = getAbbrev(teamA);
  const abbrevB = getAbbrev(teamB);

  const scoreMap = parseScoresByTeam(scores, teamA, teamB);
  const scoresA  = scoreMap[teamA] || [];
  const scoresB  = scoreMap[teamB] || [];
  const hasScore = scoresA.length > 0 || scoresB.length > 0;

  const dateStr = formatDate(date_gmt);

  const handleClick = () => {
    if (watch_url) window.open(watch_url, "_blank", "noopener,noreferrer");
  };

  // Series name: strip trailing match descriptor after comma (e.g. ", 1st ODI")
  const seriesShort = (series_name || "").split(",")[0].trim();

  return (
    <div
      onClick={handleClick}
      data-testid="sports-cricket-card"
      className={`flex-shrink-0 w-64 rounded-xl border bg-[#111] p-3.5 flex flex-col gap-2.5 transition-all duration-200 ${
        dimmed ? "opacity-55" : ""
      } ${watch_url ? "cursor-pointer hover:border-emerald-500/40 hover:bg-[#141414]" : "cursor-default"
      } ${is_live ? "border-emerald-500/30" : is_result ? "border-white/[0.05]" : "border-white/[0.08]"}`}
    >
      {/* ── Header: match type + series + status ── */}
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          {match_type && (
            <span className="text-[9px] font-bold uppercase tracking-wider text-neutral-400 bg-white/[0.07] px-1.5 py-0.5 rounded flex-shrink-0">
              {match_type}
            </span>
          )}
          {seriesShort && (
            <span className="text-[9px] text-neutral-600 truncate">{seriesShort}</span>
          )}
        </div>
        <span className={`flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold border uppercase flex-shrink-0 ${getStatusStyle(status_color)}`}>
          {is_live && <LiveDot />}
          {is_result ? "RESULT" : status_label}
        </span>
      </div>

      {/* ── Teams + Scores ── */}
      <div className="flex items-start justify-between gap-2">
        {/* Team A */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[18px] leading-none">{flagA}</span>
            <span className="text-[11px] font-bold text-white leading-tight">{abbrevA}</span>
          </div>
          {scoresA.map((s, i) => (
            <div key={i} className="text-[10px] font-mono text-emerald-300/85 leading-snug">{s}</div>
          ))}
        </div>

        {/* Center: vs + date for upcoming */}
        <div className="flex-shrink-0 flex flex-col items-center pt-1 gap-0.5">
          <span className="text-[9px] text-neutral-600 font-medium">vs</span>
          {!hasScore && dateStr && (
            <span className="text-[9px] text-neutral-600 text-center leading-tight">{dateStr}</span>
          )}
        </div>

        {/* Team B */}
        <div className="flex-1 min-w-0 text-right">
          <div className="flex items-center gap-1.5 mb-0.5 justify-end">
            <span className="text-[11px] font-bold text-white leading-tight">{abbrevB}</span>
            <span className="text-[18px] leading-none">{flagB}</span>
          </div>
          {scoresB.map((s, i) => (
            <div key={i} className="text-[10px] font-mono text-emerald-300/85 leading-snug text-right">{s}</div>
          ))}
        </div>
      </div>

      {/* ── Result summary ── */}
      {is_result && result_text && (
        <div className="text-[10px] text-neutral-500 italic border-t border-white/[0.05] pt-2 leading-snug">
          {result_text}
        </div>
      )}

      {/* ── Watch platform footer ── */}
      {watch_platform && (
        <div
          className="flex items-center gap-1.5 pt-2 border-t border-white/[0.06]"
          style={{ color: PLATFORM_COLORS[watch_platform] || PLATFORM_COLORS.default }}
        >
          <span className="text-[10px] font-bold uppercase tracking-wider">{watch_platform}</span>
          {watch_url && <span className="text-[9px] text-neutral-600 ml-auto">Watch →</span>}
        </div>
      )}
    </div>
  );
}

// ── Football Card (unchanged) ─────────────────────────────────────────────────
function FootballCard({ match, dimmed = false }) {
  const { series_name, match_type, teams, scores, team_logos,
          is_live, is_result, status_label, status_color,
          watch_platform, watch_url, date_gmt, league_logo } = match;

  const homeScore = scores?.[0]?.goals;
  const awayScore = scores?.[1]?.goals;
  const hasScore  = homeScore !== null && homeScore !== undefined;
  const dateStr   = formatDate(date_gmt);

  const handleClick = () => {
    if (watch_url) window.open(watch_url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      onClick={handleClick}
      data-testid="sports-football-card"
      className={`flex-shrink-0 w-64 rounded-xl border bg-[#111] p-3.5 flex flex-col gap-2.5 transition-all duration-200 ${
        dimmed ? "opacity-55" : ""
      } ${watch_url ? "cursor-pointer hover:border-blue-500/40 hover:bg-[#141414]" : "cursor-default"
      } ${is_live ? "border-blue-500/25" : is_result ? "border-white/[0.05]" : "border-white/[0.08]"}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {league_logo && (
            <img src={league_logo} alt="" className="w-4 h-4 object-contain flex-shrink-0" />
          )}
          <span className="text-[9px] font-bold tracking-widest text-neutral-500 uppercase truncate">
            {series_name}
          </span>
        </div>
        <span className={`flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold border uppercase flex-shrink-0 ${getStatusStyle(status_color)}`}>
          {is_live && <LiveDot />}
          {status_label}
        </span>
      </div>

      {/* Teams + Score */}
      <div className="flex items-center justify-between gap-1">
        <div className="flex flex-col items-center gap-1 flex-1">
          {team_logos?.[0] && (
            <img src={team_logos[0]} alt="" className="w-7 h-7 object-contain" />
          )}
          <span className="text-[10px] text-white font-medium text-center leading-tight">{teams?.[0]}</span>
        </div>

        <div className="flex-shrink-0 text-center px-2">
          {hasScore ? (
            <div className="text-lg font-bold text-white tabular-nums">
              {homeScore} <span className="text-neutral-500">-</span> {awayScore}
            </div>
          ) : (
            <div className="text-[10px] text-neutral-500 font-medium">vs</div>
          )}
          {match_type && (
            <div className="text-[8px] text-neutral-600 uppercase tracking-wide mt-0.5">{match_type}</div>
          )}
        </div>

        <div className="flex flex-col items-center gap-1 flex-1">
          {team_logos?.[1] && (
            <img src={team_logos[1]} alt="" className="w-7 h-7 object-contain" />
          )}
          <span className="text-[10px] text-white font-medium text-center leading-tight">{teams?.[1]}</span>
        </div>
      </div>

      {!hasScore && (
        <div className="text-[11px] text-neutral-500 text-center italic">{dateStr || "Scheduled"}</div>
      )}

      {watch_platform && (
        <div
          className="flex items-center gap-1.5 pt-2 border-t border-white/[0.06]"
          style={{ color: PLATFORM_COLORS[watch_platform] || PLATFORM_COLORS.default }}
        >
          <span className="text-[10px] font-bold uppercase tracking-wider">{watch_platform}</span>
          {watch_url && <span className="text-[9px] text-neutral-600 ml-auto">Watch →</span>}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function HomeSportsLive() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("all");
  const defaultApplied        = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sports/live`);
      setData(r.data);
      // Apply admin-set default sport only once (not on every auto-refresh)
      if (!defaultApplied.current && r.data?.default_sport && r.data.default_sport !== "all") {
        setFilter(r.data.default_sport);
        defaultApplied.current = true;
      } else if (!defaultApplied.current) {
        defaultApplied.current = true;
      }
    } catch (_) {
      setData({ live: [], upcoming: [], results: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60 s when there are live matches
  useEffect(() => {
    if (!data?.live?.length) return;
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [data, load]);

  if (loading) {
    return (
      <section className="py-6 px-4 md:px-6" data-testid="sports-live-loading">
        <div className="h-4 w-32 bg-white/[0.06] rounded animate-pulse mb-4" />
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex-shrink-0 w-64 h-36 bg-white/[0.04] rounded-xl animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  const allLive     = data?.live     || [];
  const allUpcoming = data?.upcoming || [];
  const allResults  = data?.results  || [];
  const allMatches  = [...allLive, ...allUpcoming, ...allResults];

  if (allMatches.length === 0) {
    return (
      <section className="py-5" data-testid="sports-live-section">
        <div className="px-4 md:px-6 mb-3 flex items-center gap-2">
          <span className="text-sm font-bold text-white tracking-wide uppercase">Sports</span>
        </div>
        <div className="px-4 md:px-6">
          <p className="text-xs text-neutral-600">No live or upcoming matches right now. Check back soon.</p>
        </div>
      </section>
    );
  }

  const filtered = filter === "all"
    ? allMatches
    : allMatches.filter(m => m.sport === filter);

  const liveCount    = allLive.filter(m => filter === "all" || m.sport === filter).length;
  const hasLiveAny   = allLive.length > 0;
  const hasFootball  = allMatches.some(m => m.sport === "football");
  const filters      = hasFootball ? ["all", "cricket", "football"] : ["all", "cricket"];
  const filterLabel  = { all: "All", cricket: "Cricket 🏏", football: "Football ⚽" };

  const isStale      = data?.cricket_stale === true;
  const staleMinutes = data?.cricket_cache_age_minutes;

  const filtLive     = filtered.filter(m => m.is_live);
  const filtUpcoming = filtered.filter(m => m.is_upcoming);
  const filtResults  = filtered.filter(m => m.is_result);

  return (
    <section className="py-5" data-testid="sports-live-section">
      {/* Header */}
      <div className="px-4 md:px-6 mb-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {hasLiveAny && <LiveDot />}
            <h2 className="text-sm font-bold text-white tracking-wide uppercase">
              {hasLiveAny ? "Live Sports" : "Sports"}
            </h2>
          </div>
          <div className="flex items-center gap-1.5">
            {filters.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`sports-filter-${f}`}
                className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold border transition-all ${
                  filter === f
                    ? "bg-amber-500/20 border-amber-500/35 text-amber-400"
                    : "bg-white/[0.04] border-white/[0.08] text-neutral-500 hover:text-neutral-300"
                }`}
              >
                {filterLabel[f]}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] text-neutral-600">
            {liveCount > 0 ? `${liveCount} LIVE` : `${filtered.filter(m => !m.is_result).length} upcoming`}
          </span>
          {isStale && data?.cricket_total > 0 && (
            <span
              title="Cricket scores are from cache — live API quota exceeded. Will auto-refresh."
              className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500/80 text-[9px] font-medium cursor-help"
              data-testid="sports-stale-badge"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500/60 animate-pulse" />
              {data?.cricket_snapshot_age_days != null
                ? `Snapshot · ${data.cricket_snapshot_age_days < 1 ? "today" : `${data.cricket_snapshot_age_days}d ago`}`
                : staleMinutes != null
                  ? `Updated ${staleMinutes < 1 ? "<1" : staleMinutes} min ago`
                  : "Cached data"}
            </span>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="px-4 md:px-6">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-xs text-neutral-500">
            <span>{filter === "cricket" ? "🏏" : "⚽"}</span>
            <span>No live {filter} matches right now. Check back soon.</span>
          </div>
        </div>
      ) : (
        <>
          {(filtLive.length > 0 || filtUpcoming.length > 0) && (
            <div className="px-4 md:px-6 flex gap-3 overflow-x-auto pb-2 scrollbar-hide" data-testid="sports-scroll-rail">
              {[...filtLive, ...filtUpcoming].map(match =>
                match.sport === "cricket"
                  ? <CricketCard key={match.id} match={match} />
                  : <FootballCard key={match.id} match={match} />
              )}
            </div>
          )}

          {filtResults.length > 0 && (
            <div className="mt-3">
              <div className="px-4 md:px-6 mb-2">
                <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-600">Recent Results</span>
              </div>
              <div className="px-4 md:px-6 flex gap-3 overflow-x-auto pb-1 scrollbar-hide" data-testid="sports-results-rail">
                {filtResults.map(match =>
                  match.sport === "cricket"
                    ? <CricketCard key={match.id} match={match} dimmed />
                    : <FootballCard key={match.id} match={match} dimmed />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
